import Hearing from "@mui/icons-material/Hearing";
import HearingOutlined from "@mui/icons-material/HearingOutlined";
import HiveOutlined from "@mui/icons-material/HiveOutlined";
import HubIcon from "@mui/icons-material/Hub";
import Inbox from "@mui/icons-material/Inbox";
import InboxOutlined from "@mui/icons-material/InboxOutlined";
import Language from "@mui/icons-material/Language";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import PermMedia from "@mui/icons-material/PermMedia";
import PieChartOutlineRounded from "@mui/icons-material/PieChartOutlineRounded";
import Send from "@mui/icons-material/Send";
import Task from "@mui/icons-material/Task";
import TaskOutlined from "@mui/icons-material/TaskOutlined";
import Timeline from "@mui/icons-material/Timeline";
import TipsAndUpdates from "@mui/icons-material/TipsAndUpdates";
import WebAsset from "@mui/icons-material/WebAsset";
import { usePendingReviewCount } from "../../../../../pages/reviews/hooks/use-pending-review-count";
import { usePermissions } from "../../../../../server/auth/use-permissions";
import { useFlag } from "../../../../../server/optimizely";
import { knowledgeHubUrl } from "../../../../../utils/knowledge-hub-url";
import { useAccountLimitsContext } from "../../../../context/account-limit-context";
import { useAnalyticsPerms } from "../../../../employees/hooks/use-analytics";
import { useAccount } from "../../../../generic/hooks/use-account";
import { useClaims } from "../../../../generic/hooks/use-claims";
import { useSidebar2026 } from "../../../hooks/use-sidebar-2026";
import { useInboxCount } from "../hooks/use-inbox-count";
import { useIsActive } from "../hooks/use-is-active";
import { useReviewCountRefreshKey } from "../hooks/use-review-count-refresh-key";
import { Item } from "../item";
import { Programs } from "../programs";
import { Section } from "../section";
import { PlannerItems } from "./nav-planner-options";
import { NavbarBadge } from "./navbar-badge";

import type { FC } from "react";

interface NavOptionsProps {
  onNavigate?: () => void;
}

export const NavOptions: FC<NavOptionsProps> = ({ onNavigate }) => {
  const { data: account } = useAccount();
  const [cha_4274_landing_page_builder] = useFlag("cha_4274_landing_page_builder");
  const [exos] = useFlag("cha-4488_exos");
  const [labs] = useFlag("labs");
  const [reviewEnabled] = useFlag("reviewer_role");

  const isActive = useIsActive();
  const perms = usePermissions();
  const sidebar2026 = useSidebar2026();
  const claims = useClaims();

  const analyticsPerms = useAnalyticsPerms();
  const isSuperAdmin = perms.super_admin;
  const isCeAdmin = perms.ce_admin;

  const { listen_entitlement, enable_knowledge_hub, communication_insights } =
    useAccountLimitsContext();

  const { count: unread_conversation_count } = useInboxCount(isSuperAdmin);
  const reviewCountRefreshKey = useReviewCountRefreshKey();
  const { data: pendingReviewCount } = usePendingReviewCount(reviewEnabled, reviewCountRefreshKey);

  return (
    <Section>
      <>
        <Item
          data-analytics-id="sidebar-content-library"
          title="Content Library"
          icon={<PermMedia />}
          onClick={{ type: "href", value: "/content-library" }}
          onNavigate={onNavigate}
          active={isActive("/content-library")}
        />
        {reviewEnabled && (
          <Item
            data-analytics-id="sidebar-reviews"
            title="Approvals"
            icon={sidebar2026 ? <TaskOutlined /> : <Task />}
            onClick={{ type: "href", value: "/reviews" }}
            onNavigate={onNavigate}
            active={isActive("/reviews", true)}
            label={
              Boolean(pendingReviewCount) && (
                <NavbarBadge color="primary" badgeContent={pendingReviewCount} />
              )
            }
          />
        )}
        <Item
          data-analytics-id="sidebar-discover"
          title="Discover"
          icon={<Language />}
          onClick={{ type: "href", value: "/discover" }}
          onNavigate={onNavigate}
          active={isActive("/discover", true)}
        />
        <Item
          data-analytics-id="sidebar-listen"
          title="Listen"
          icon={sidebar2026 ? <HearingOutlined /> : <Hearing />}
          onClick={{ type: "href", value: "/listen" }}
          onNavigate={onNavigate}
          active={isActive("/listen", true)}
          label={listen_entitlement === "Pro" ? null : <NavbarBadge badgeContent="New" />}
        />
        <Programs />
        <Item
          data-analytics-id="sidebar-send-communications"
          title="Sent Communications"
          icon={<Send />}
          onClick={{ type: "href", value: "/moments/history" }}
          onNavigate={onNavigate}
          active={isActive("/moments/history")}
        />
        {isSuperAdmin && communication_insights && (
          <Item
            data-analytics-id="sidebar-insights"
            title="Insights"
            icon={sidebar2026 ? <TipsAndUpdates /> : <LightbulbOutlined />}
            onClick={{ type: "href", value: "/insights" }}
            onNavigate={onNavigate}
            active={isActive("/insights")}
          />
        )}
        {analyticsPerms.length > 0 && (
          <Item
            data-analytics-id="sidebar-analytics"
            title="Analytics"
            icon={<PieChartOutlineRounded />}
            onClick={{ type: "href", value: "/analytics" }}
            onNavigate={onNavigate}
            active={isActive("/analytics")}
          />
        )}

        {isSuperAdmin && (
          <Item
            data-analytics-id="sidebar-recipient-activity"
            title="Recipient Activity"
            icon={<Timeline />}
            onClick={{ type: "href", value: "/recipient-activity" }}
            onNavigate={onNavigate}
            active={isActive("/recipient-activity", true)}
          />
        )}

        {isSuperAdmin && (
          <Item
            data-analytics-id="sidebar-inbox"
            title="Inbox"
            icon={sidebar2026 ? <InboxOutlined /> : <Inbox />}
            onClick={{ type: "href", value: "/inbox" }}
            onNavigate={onNavigate}
            active={isActive("/inbox", true)}
            label={
              account && !account.enable_twilio ? (
                <NavbarBadge color="primary" badgeContent="New" />
              ) : unread_conversation_count ? (
                <NavbarBadge color="primary" badgeContent={unread_conversation_count} />
              ) : null
            }
          />
        )}

        {cha_4274_landing_page_builder && (
          <Item
            data-analytics-id="sidebar-pages"
            title="Pages"
            icon={<WebAsset />}
            onClick={{ type: "href", value: "/pages" }}
            onNavigate={onNavigate}
            active={isActive("/pages", true)}
            label={<NavbarBadge badgeContent="Beta" />}
          />
        )}
        {isSuperAdmin && exos && (
          <Item
            data-analytics-id="sidebar-pages"
            title="EXOS"
            icon={<HiveOutlined />}
            onClick={{ type: "href", value: "/exos" }}
            onNavigate={onNavigate}
            active={isActive("/exos", true)}
            label={<NavbarBadge badgeContent="Beta" />}
          />
        )}
        {isSuperAdmin && labs && (
          <Item
            data-analytics-id="sidebar-pages"
            title="Labs"
            icon={<WebAsset />}
            onClick={{ type: "href", value: "/labs" }}
            onNavigate={onNavigate}
            active={isActive("/labs", true)}
            label={<NavbarBadge badgeContent="Beta" />}
          />
        )}

        {enable_knowledge_hub && isCeAdmin && claims?.org_name && (
          <Item
            data-analytics-id="sidebar-knowledge-hub"
            title="KnowledgeHub"
            icon={<HubIcon />}
            onClick={{ type: "href", value: knowledgeHubUrl(claims.org_name) }}
            onNavigate={onNavigate}
            active={() => false}
            label={<NavbarBadge badgeContent="Beta" />}
          />
        )}

        {/* The 2026 sidebar is one list: Calendar and Timeline end it, rather than a Planner section. */}
        {sidebar2026 && <PlannerItems onNavigate={onNavigate} />}
      </>
    </Section>
  );
};
